import {
  Button,
  Check,
  Form,
  FormCard,
  FormItem,
  PageHeader,
  Select,
  TextArea,
  Trash,
  theme,
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { useTranslation } from 'react-i18next'
import {
  ButtonsContainer,
  PageWrapper,
  DateAndTimeRowPicker,
} from '../../components'
import { Form as AntdForm } from 'antd'
import DeleteIndividualAbsenceModal from './modals/DeleteIndividualAbsenceModal'
import { useState } from 'react'
import { getRequiredRule } from 'screens/Rostering/utils'
import { useNavigate, useParams } from 'react-router-dom'
import useUser from 'hooks/useUser/useUser'
import { Spinner } from 'components/generic'
import dayjs from 'dayjs'
import { showToast } from 'common/utils'
import { useAbsenceDetail } from 'screens/Rostering/hooks/useAbsenceDetail'
import { useAbsenceUpdate } from 'screens/Rostering/hooks/useAbsenceUpdate'
import { useAbsenceDelete } from 'screens/Rostering/hooks/useAbsenceDelete'
import { useAbsenceTypesList } from 'screens/Rostering/hooks/useAbsenceTypesList'

const EditIndividualAbsenceScreen = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [form] = AntdForm.useForm()
  const [isModalVisible, setIsModalVisible] = useState(false)

  const { absenceId } = useParams()
  const { absence, isAbsenceLoading } = useAbsenceDetail({ absenceId })
  const { updateAbsence, isUpdateLoading } = useAbsenceUpdate({
    absenceId,
    onUpdateSuccess: () => {
      showToast({ title: t('ABSENCE_EDITED') })
      navigate(-1)
    },
  })
  const { deleteAbsence, isDeleteLoading } = useAbsenceDelete({
    absenceId,
    onDeleteSuccess: () => {
      showToast({ title: t('ABSENCE_DELETED') })
      navigate(-2)
    },
  })
  const { user, isLoading: isUserLoading } = useUser({ id: absence?.userId })
  const { absenceTypes, isAbsenceTypesLoading } = useAbsenceTypesList()

  const absenceType = absenceTypes.find(
    (absenceType) => absenceType.id === absence?.absenceType
  )

  const initialValues = {
    ...absence,
    user: `${user?.name.first} ${user?.name.last}`,
    'startDate.date': dayjs(absence?.startDate),
    'startDate.time': dayjs(absence?.startDate),
    'endDate.date': dayjs(absence?.endDate),
    'endDate.time': dayjs(absence?.endDate),
  }

  const absenceTypesOptions = absenceTypes
    ?.filter((absenceType) => !absenceType.isDeleted)
    .map((absenceType) => ({
      value: absenceType.id,
      label: absenceType.name,
    }))

  const handleOnFinish = async (values: any) => {
    const endDate = dayjs(values['endDate.date'])
      .set('hour', dayjs(values['endDate.time']).hour())
      .set('minute', dayjs(values['endDate.time']).minute())
      .set('second', dayjs(values['endDate.time']).second())
      .toISOString()

    const startDate = dayjs(values['startDate.date'])
      .set('hour', dayjs(values['startDate.time']).hour())
      .set('minute', dayjs(values['startDate.time']).minute())
      .set('second', dayjs(values['startDate.time']).second())
      .toISOString()

    try {
      await updateAbsence({
        absenceType: values.absenceType,
        userId: user?.id,
        startDate,
        endDate,
        absenceNotes: values?.absenceNotes,
        instancesUnallocated: 0,
      })
    } catch (e: any) {
      showToast({
        title: e?.message,
        type: 'error',
      })
    }
  }

  if (isAbsenceLoading || isAbsenceTypesLoading || isUserLoading) {
    return <Spinner />
  }

  return (
    <>
      <PageHeader
        title={`${t('EDIT')} ${t('INDIVIDUAL_ABSENCE_TITLE', {
          user: `${user?.name.first} ${user?.name.last}`,
          type: absenceType?.name,
        })}`}
        sticky
      >
        <ButtonsContainer>
          <Button onClick={() => navigate(-1)}>{t('CANCEL')}</Button>
          <Button variant="danger" onClick={() => setIsModalVisible(true)}>
            <Trash />
            {t('DELETE')}
          </Button>
          <Button
            isLoading={isUpdateLoading}
            variant="primary"
            onClick={form.submit}
          >
            <Check color={theme.color.neutral.color11} />
            {t('SAVE')}
          </Button>
        </ButtonsContainer>
      </PageHeader>
      <PageWrapper>
        <Form
          form={form}
          onFinish={handleOnFinish}
          initialValues={initialValues}
          onFinishFailed={() => {}}
        >
          <FormCard>
            <FormItem
              name="user"
              label={<Text>{t('TEAM_MEMBER')}</Text>}
              rules={[getRequiredRule(t('TEAM_MEMBER'))]}
            >
              <Select disabled />
            </FormItem>
            <FormItem
              name="absenceType"
              label={<Text>{t('ABSENCE_TYPE')}</Text>}
              rules={[getRequiredRule(t('ABSENCE_TYPE'))]}
            >
              <Select options={absenceTypesOptions} />
            </FormItem>
            <DateAndTimeRowPicker
              label={<Text>{t('START_DATE')}</Text>}
              name="startDate"
              required
            />
            <DateAndTimeRowPicker
              label={<Text>{t('END_DATE')}</Text>}
              name="endDate"
              required
            />
          </FormCard>
          <FormCard title={t('ADDITIONAL_DETAILS')}>
            <FormItem name="absenceNotes" label={<Text>{t('NOTES')}</Text>}>
              <TextArea
                autoSize={{
                  minRows: 3,
                }}
              />
            </FormItem>
          </FormCard>
        </Form>
      </PageWrapper>
      {isModalVisible && (
        <DeleteIndividualAbsenceModal
          isModalVisible={isModalVisible}
          setIsModalVisible={setIsModalVisible}
          deleteAbsence={deleteAbsence}
          isDeleteLoading={isDeleteLoading}
          userName={`${user?.name.first} ${user?.name.last}`}
          absenceType={absenceType}
        />
      )}
    </>
  )
}

export default EditIndividualAbsenceScreen
